import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { finishSurvey, getReferences, publishReport, getSurveySession, updateSurveySession } from '../../api/digiscore-api';
import { useTranslation } from "react-i18next";
import './SurveyResultPage.css';
import ResultFilter from '../../components/ResultFilter/ResultFilter';
import SurveyResultComponent from '../../components/SurveyResultComponent/SurveyResultComponent';
import ScoreComparisonTable from '../../components/ScoreComparisonTable/ScoreComparisonTable';
import SurveyTable from "../../components/SurveyTable/SurveyTableComponent"
import { computeAllScores } from "../../utils/scoringHelpers.js";
import Joyride from 'react-joyride';
import { Backdrop, CircularProgress } from "@mui/material";
import _ from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { useAuth0 } from "@auth0/auth0-react";
import Loading from '../../components/Loading/Loading';

function sentenceCase(input) {
  const s = (input ?? '').toString().trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function decodeParams(params) {
  let finalString = '';
  finalString = decodeURIComponent(params);
  finalString = finalString.replace(/&amp;/g, '&');
  finalString = finalString.replace(/&nbsp;/g, '');
  finalString = finalString.replaceAll(/\s/g, '');
  return finalString;
}

function getParams(url) {
  const vars = url.split('&');
  const paramsArray = {};
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    paramsArray[pair[0]] = decodeURIComponent(pair[1]);
  }
  return paramsArray;
}

function findReferenceIndex(references, namespace, dimension, category) {
  if (!references) return undefined;

  const year = namespace === "BaaS20E" ? 2020 : 2022;

  return references.findIndex((ref) => ref.year = year && ref.dimension === dimension && ref.category === category);
}

function useViewportWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

function HowToReadModal({ open, onClose }) {
  const { t } = useTranslation();
  const tabs = useMemo(() => ([
    { key: 'chapter1', label: t('result.content.chapter1.title') },
    { key: 'chapter2', label: t('result.content.chapter2.title') },
    { key: 'chapter3', label: t('result.content.chapter3.title') },
  ]), [t]);

  const [activeIndex, setActiveIndex] = useState(0);

  if (!open) return null;

  const TabButton = ({ index, children }) => (
    <button
      role="tab"
      aria-selected={activeIndex === index}
      className={`howto__tab ${activeIndex === index ? 'is-active' : ''}`}
      onClick={() => setActiveIndex(index)}
      type="button"
    >
      {children}
    </button>
  );

  return (
    <div className="howto__backdrop" onClick={onClose}>
      <div
        className="howto__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="howto-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="howto__header">
          <div className="howto__title">
            <img src="/icon-text.svg" alt="" className="icon-text" />
            <h2 id="howto-title">{t('result.content.title')}</h2>
          </div>
          <button className="howto__close" type="button" aria-label={t('header.close') || 'Fermer'} onClick={onClose}>×</button>
        </div>

        <div className="howto__tabs" role="tablist" aria-label="Sections d'aide">
          {tabs.map((tab, i) => (
            <TabButton key={tab.key} index={i}>{tab.label}</TabButton>
          ))}
        </div>

        <div className="howto__body">
          {activeIndex === 0 && (
            <section>
              <p>{t('result.content.chapter1.p1')}</p>
              <p>{t('result.content.chapter1.p2')}</p>
              <p>{t('result.content.chapter1.p3')}</p>
              <p>{t('result.content.chapter1.p4')}</p>

              <h5 className="howto__topics-title">{t("result.content.table.head.th2")}</h5>
              <ul className="howto__list">
                <li className="howto__list-item">
                  <img className="howto__list-icon" src="/icon-arrow-right.svg" alt="" />
                  <div className="howto__list-block">
                    <div className="howto__list-title">
                      {t('result.content.chapter1.li1.title')}
                    </div>
                    <div className="howto__list-desc">
                      {t('result.content.chapter1.li1.content')}
                    </div>
                  </div>
                </li>

                <li className="howto__list-item">
                  <img className="howto__list-icon" src="/icon-arrow-right.svg" alt="" />
                  <div className="howto__list-block">
                    <div className="howto__list-title">
                      {t('result.content.chapter1.li2.title')}
                    </div>
                    <div className="howto__list-desc">
                      {t('result.content.chapter1.li2.content')}
                    </div>
                  </div>
                </li>

                <li className="howto__list-item">
                  <img className="howto__list-icon" src="/icon-arrow-right.svg" alt="" />
                  <div className="howto__list-block">
                    <div className="howto__list-title">
                      {t('result.content.chapter1.li3.title')}
                    </div>
                    <div className="howto__list-desc">
                      {t('result.content.chapter1.li3.content')}
                    </div>
                  </div>
                </li>

                <li className="howto__list-item">
                  <img className="howto__list-icon" src="/icon-arrow-right.svg" alt="" />
                  <div className="howto__list-block">
                    <div className="howto__list-title">
                      {t('result.content.chapter1.li4.title')}
                    </div>
                    <div className="howto__list-desc">
                      {t('result.content.chapter1.li4.content')}
                    </div>
                  </div>
                </li>
              </ul>

            </section>
          )}

          {activeIndex === 1 && (
            <section>
              <p>{t('result.content.chapter2.p1')}</p>
              <p>{t('result.content.chapter2.p2')}</p>
              <p>{t('result.content.chapter2.p3')}</p>
              <p>{t('result.content.chapter2.p4')}</p>
            </section>
          )}

          {activeIndex === 2 && (
            <section>
              <p>{t('result.content.chapter3.p1')}</p>
              <p>{t('result.content.chapter3.p2')}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function YourResultPanel({ t, survey, pdfUrl, onDownload, onCopyLink, infoTipOpen, onOpenHowTo }) {
  const navigate = useNavigate();
  const rows = [
    { key: 'infrastructure', label: t('result.axis.infrastructure'), value: survey?.infrastructure },
    { key: 'humanResources', label: t('result.axis.organization'), value: survey?.humanResources },
    { key: 'processFlow', label: t('result.axis.processes'), value: survey?.processFlow },
    { key: 'salesProducts', label: t('result.axis.strategy'), value: survey?.salesProducts },
  ];
  const global = survey?.globalScore ?? survey?.scoreGlobal ?? survey?.global ?? survey?.score;
  const completedStr = survey?.completedAt ? new Date(survey.completedAt).toLocaleDateString('fr-BE') : '—';

  const linkBtnStyle = {
    background: 'transparent',
    border: 0,
    padding: 0,
    marginTop: 18,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#ffffff',
    cursor: 'pointer',
    textDecoration: 'none',
    marginLeft: "7px",
  };

  return (
    <aside className="result__right">
      <div className="result__card">
        <div className="result__card-title">{t('result.sidebar.title')}</div>
        <div className="createdOn">{t('result.sidebar.createdOn')} : <strong>{completedStr}</strong></div>

        <ul className="result__card-list">
          {rows.map(r => (
            <li key={r.key}>
              <span>{r.label}</span>
              <strong>{Number.isFinite(+r.value) ? +r.value : '-'}</strong>
            </li>
          ))}
          <li className="result__card-total">
            <span>{t('result.title')}</span>
            <strong>{Number.isFinite(+global) ? +global : '-'}</strong>
          </li>
        </ul>

        <button
          className="btn btn-alt downloadTour result__card-btn"
          onClick={pdfUrl ? onDownload : undefined}
          disabled={!pdfUrl}
          aria-busy={!pdfUrl}
        >
          {pdfUrl ? (
            <>
              <img src="/icon-download.svg" alt="" className="result__card-btn-icon" />
              {t('result.options.download')}
            </>
          ) : (
            <>
              <span style={{ marginRight: 8 }}>{t('loading.pdf')}</span>
              <CircularProgress size={16} sx={{ marginLeft: 0, color: '#fff' }} />
            </>
          )}
        </button>

        <div className="result__card-btnwrap">
          <button
            type="button"
            onClick={onCopyLink}
            aria-describedby="copy-bubble"
            className="shareTour"
            style={linkBtnStyle}
          >
            <img src="/icon-share.svg" alt="" className="result__card-btn-icon" />
            {t('result.options.link')}
          </button>

          <div
            id="copy-bubble"
            className={`copy-bubble ${infoTipOpen ? 'open' : ''}`}
            role="status"
            aria-live="polite"
          >
            {t('result.options.copy')}
          </div>
        </div>

        <button
          className="btn btn-alt downloadTour result__card-btn"
          onClick={onOpenHowTo}
        >
          {t('result.content.title')}
        </button>

        <button
          className="btn btn-alt downloadTour result__card-btn"
          onClick={() => navigate('/ressources')}
        >
          {t('result.content.ressource.content')}
        </button>
      </div>
    </aside>
  );
}

function SurveyResultPage(props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [enterprise, setEnterprise] = useState({});
  const [survey, setSurvey] = useState({});
  const [enterpriseCompare, setEnterpriseCompare] = useState(null);
  const [surveyCompare, setSurveyCompare] = useState(null);
  const [references, setReferences] = useState({});
  const [currentReferenceIndex, setCurrentReferenceIndex] = useState();
  const [histogramData, setHistogramData] = useState([]);
  const [histogramLabels, setHistogramLabels] = useState([]);
  const [isInfoTipOpen, setInfoTipOpen] = useState(false);
  const [showJoyride, setShowJoyride] = useState(false);
  const [pdfUrl, setPdfUrl] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [filterTitle, setFilterTitle] = useState();
  const [filtered, setFiltered] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const vw = useViewportWidth();
  const titleRightPadding = vw >= 992 ? '284px' : '0px';

  const { params } = useParams();

  const data = [
    {
      infrastructure: references[currentReferenceIndex]?.infrastructure?.Mean,
      organization: references[currentReferenceIndex]?.humanResources?.Mean,
      processes: references[currentReferenceIndex]?.processFlow?.Mean,
      strategy: references[currentReferenceIndex]?.salesProducts?.Mean,
      name: t('result.histogram.mean'),
    },
    {
      infrastructure: survey?.infrastructure,
      organization: survey?.humanResources,
      processes: survey?.processFlow,
      strategy: survey?.salesProducts,
      name: `${enterprise?.name} (${new Date(survey.completedAt).toLocaleDateString()})`,
    },
  ];

  if (
    surveyCompare &&
    enterpriseCompare &&
    [surveyCompare.infrastructure, surveyCompare.humanResources, surveyCompare.processFlow, surveyCompare.salesProducts]
      .some(v => typeof v === 'number' && !Number.isNaN(v))
  ) {
    data.push({
      infrastructure: surveyCompare?.infrastructure,
      organization: surveyCompare?.humanResources,
      processes: surveyCompare?.processFlow,
      strategy: surveyCompare?.salesProducts,
      name: `${enterpriseCompare?.name} (${new Date(surveyCompare?.completedAt).toLocaleDateString()})`,
    })
  }

  const steps = [
    {
      target: '.result',
      content: t('result.tourGuide.one'),
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
      styles: {
        options: {
          arrowColor: '#fff',
          backgroundColor: '#fff',
          borderRadius: '25px',
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
        },
        tooltip: {
          marginTop: '-10px',
        },
      },
    },
    {
      target: '.result__title',
      content: t('result.tourGuide.two'),
      placement: 'top',
    },
    {
      target: '.highligth-bar',
      content: t('result.tourGuide.three'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.chart-histogram',
      content: t('result.tourGuide.four'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.chart-radar',
      content: t('result.tourGuide.five'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.filterTour',
      content: t('result.tourGuide.six'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.info1Tour',
      content: t('result.tourGuide.seven'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.readResourceTour',
      content: t('result.tourGuide.height'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.downloadTour',
      content: t('result.tourGuide.nine'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
    {
      target: '.shareTour',
      content: t('result.tourGuide.ten'),
      placement: 'top',
      spotlightClicks: true,
      styles: { options: { zIndex: 10000 } }
    },
  ];

  const { isAuthenticated, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const localToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

    if (localToken && !isAuthenticated) {
      try {
        const decoded = jwtDecode(localToken);
        const exp = decoded.exp * 1000;
        const now = Date.now();

        if (exp > now) {
          window.__puppeteerAuth = true;
        } else {
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_token');
        }
      } catch (err) {
        console.warn('Erreur de parsing du token local', err);
      }
    }

    if (!isAuthenticated && !localToken) {
      loginWithRedirect();
    }
  }, [isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (survey?.namespace && !filtered) {
      const year =
        survey.namespace === "BAS20E" ? 2020 :
        survey.namespace === "BAS22E" ? 2022 :
        survey.namespace === "BAS25E" ? 2025 :
      null;
      setFilterTitle(t("result.subtitles.global", { year }));
    }
  }, [i18n.language, survey?.namespace, t, filtered]);

  useEffect(() => {
    const handler = () => setShowJoyride(true);
    window.addEventListener("digiscore:result:openTour", handler);
    return () => window.removeEventListener("digiscore:result:openTour", handler);
  }, []);

  useEffect(() => {
  const fetchData = async () => {
    try {
      if (isGenerating || pdfUrl) {
        return;
      }
      setIsGenerating(true);

      const decodedParams = getParams(decodeParams(params));

      let enterpriseResult;
      let enterpriseCompareResult;

      if (decodedParams.LOG && decodedParams.CT) {
        enterpriseResult = await finishSurvey(decodedParams.LOG, decodedParams.CT, "BaaS22E", props.token);
      } else if (decodedParams.CUR && decodedParams.COMP) {
        const cur = await getSurveySession(decodedParams.CUR, props.token);
        const comp = await getSurveySession(decodedParams.COMP, props.token);
        enterpriseResult = cur;
        enterpriseCompareResult = comp;
      } else {
        enterpriseResult = await getSurveySession(params, props.token);
      }

      if (enterpriseResult) {
        const normalized = Object.keys(enterpriseResult).reduce((acc, key) => {
          acc[key.toLowerCase()] = enterpriseResult[key];
          return acc;
        }, {});

        const aliases = {
          infrastructure: ['infrastructure'],
          humanresources: ['humanresources', 'humanressources'],
          processflow: ['processflow'],
          salesproducts: ['salesproducts'],
          globalscore: ['globalscore', 'scoreglobal', 'global', 'score'],
        };

        const pickField = (canonKey) => {
          const keys = aliases[canonKey] || [canonKey];
          for (const k of keys) {
            if (k in normalized) return normalized[k];
          }
          return undefined;
        };

        const missing = Object.keys(aliases).filter((canonKey) => {
          const v = pickField(canonKey);
          if (v == null) return true;
          if (typeof v === 'object') {
            const val = v.Value ?? v.value;
            return val == null;
          }
          return false;
        });

        if (missing.length) {

          const responses = enterpriseResult?.responses;
          const scores = await computeAllScores(responses);

          function toPercent(value, max) {
            if (!value || !max) return 0;
            return Number(((value / max) * 100).toFixed(1));
          }

          const infra = toPercent(scores.totalInfrastructure, scores.totalInfrastructureMax);
          const humain = toPercent(scores.totalHumain, scores.totalHumainMax);
          const processus = toPercent(scores.totalProcessus, scores.totalProcessusMax);
          const strategie = toPercent(scores.totalStrategie, scores.totalStrategieMax);

          let global = Number(((infra + humain + processus + strategie) / 4).toFixed(1));

          if (global > 89.9) global = 89.9;

          const payload = {
            Infrastructure: { Value: infra, Code: 'IN' },
            HumanResources: { Value: humain, Code: 'RH' },
            ProcessFlow: { Value: processus, Code: 'PF' },
            SalesProducts: { Value: strategie, Code: 'SP' },
            GlobalScore: { Value: global, Code: 'GS' }
          };


          try {
            console.log(scores);
            await updateSurveySession(enterpriseResult._id, payload, props.token);
            const updated = await getSurveySession(enterpriseResult._id, props.token);
            setSurvey(updated);
            enterpriseResult = updated;
          } catch (err) {
            console.error('Erreur lors du PATCH SurveySession:', err);
          }
        } else {}
      }

      setEnterprise(enterpriseResult?.enterprise);
      setEnterpriseCompare(enterpriseCompareResult ? enterpriseCompareResult.enterprise : null);
      if (enterpriseResult) {
        setSurvey(enterpriseResult);
      }
      setSurveyCompare(enterpriseCompareResult ? enterpriseCompareResult : null);

      const response = await getReferences(props.token);
      if (response.length > 1) {
        const refIndex = findReferenceIndex(response, enterpriseResult?.namespace, 'Global', 'GLOB');
        setReferences(response);
        setCurrentReferenceIndex(refIndex);
        if (enterpriseResult) {
          const ns = enterpriseResult?.namespace;
          const year = ns === "BaaS20E" ? 2020 : 2022;
          setFilterTitle(t("result.subtitles.global", { year }));
        }
        const scores = Object.values(response[refIndex].score).map(s => parseFloat(s));
        const labels = Object.keys(response[refIndex].score);
        setHistogramData(scores);
        setHistogramLabels(labels);
      }

      setIsLoading(false);

      setIsLoading(false);
      if (enterpriseResult) {
        const { _id, namespace } = enterpriseResult;
        const url = await publishReport(_id, enterpriseResult.enterprise.bce, namespace, props.token);
        setPdfUrl(url);
      }

    } catch (error) {
      console.error('fetchData error:', error);
      navigate('/error');
    } finally {
      setIsGenerating(false);
    }
  };

  fetchData();
  // eslint-disable-next-line
}, [params, props.token]);


  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenGuide');
    if (!hasSeenGuide) {
      setShowJoyride(true);
      localStorage.setItem('hasSeenGuide', 'true');
    }
  }, []);

  function copyShareLinkToClipboard() {
    try {
      navigator.clipboard.writeText(window.location.href);
      setInfoTipOpen(true);
      setTimeout(() => setInfoTipOpen(false), 1100);
    } catch (e) {
      console.error('Clipboard error:', e);
    }
  }

  async function changeReference(dimension, category, titleKey) {
    const refIndex = findReferenceIndex(references, survey?.namespace, dimension, category);

    setCurrentReferenceIndex(refIndex);
    setFiltered(true);
    setFilterTitle(t(`result.subtitles.${titleKey}`, { value: t(`${titleKey}.${category}`).toLowerCase(), year: survey.namespace === 'BaaS22E' ? 2022 : 2020, interpolation: { escapeValue: false } }))
    const scores = Object.values(references[refIndex].score).map(s => parseFloat(s));
    const labels = Object.keys(references[refIndex].score);
    setHistogramData(scores);
    setHistogramLabels(labels);
  }

  function removeFilter() {
    changeReference('Global', 'GLOB', 'global');
    setFiltered(false);
  }

  async function downloadReportHandler() {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert(t("result.alert.reportNotReady"));
    }
  }

  if (!isAuthenticated && !window.__puppeteerAuth) {
    return (
      <Backdrop
        sx={{ color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }
  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <Loading />
      </Backdrop>
    )
  }

  return (
    <div className='result'>
      <Joyride
        steps={steps}
        continuous
        spotlightClicks
        showSkipButton
        scrollToFirstStep
        showProgress
        styles={{
          overlay: {
            height: document.body.scrollHeight
          }
        }}
        locale={{
          back: t("result.tourGuide.back"),
          close: t("result.tourGuide.close"),
          last: t("result.tourGuide.last"),
          next: t("result.tourGuide.next"),
          pause: t("result.tourGuide.pause"),
          resume: t("result.tourGuide.resume"),
          skip: t("result.tourGuide.skip"),
          start: t("result.tourGuide.start"),
          stop: t("result.tourGuide.stop"),
          prev: t("result.tourGuide.prev"),
          done: t("result.tourGuide.done"),
        }}
        run={showJoyride}
        callback={(data) => {
          if (data.status === 'finished' || data.status === 'skipped') {
            setShowJoyride(false);
          }
        }}
      />

      <div className='result__content'>
        
        <div className="result__title" style={{ lineHeight: 1 }}>
          <h2
            style={{
              margin: 0,
              fontSize: '36px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '6px',
              paddingRight: titleRightPadding
            }}
          >
            <img
              src="/icon-arrow-target.png"
              alt=""
              style={{
                width: 90,
                height: 90,
                objectFit: 'contain',
                display: 'inline-block',
                flexShrink: 0,
                marginTop: 4
              }}
            />
            <span style={{ whiteSpace: 'nowrap' }}>
              {t('result.title')}
            </span>
            <span
              style={{
                color: '#0A93D2',
                minWidth: 0,
                wordBreak: 'break-word',
                flex: 1
              }}
            >
              maison {enterprise?.name}
            </span>
          </h2>
        </div>

        <div className="result__layout">

          <aside className='result__left filter filterTour'>
            <div className="filter-options filters--sidebar">
              <ul>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.sector')} options={ _.pickBy(t('sector', { returnObjects: true }), (value, key)=> { return (_.startsWith(key, "C") || _.startsWith(key, "S")) && !_.startsWith(key, "C18") }) } dimension='Secteur' onOptionChange={(dimension, category) => changeReference(dimension, category, 'sector')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.size')} options={t('size', { returnObjects: true })} dimension='Taille' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'size')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.locality')} options={t('province', { returnObjects: true })} dimension='Province' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'province')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.sites')} options={t('site', { returnObjects: true })} dimension='Sites' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'site')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.targetMarket')} options={t('targetMarket', { returnObjects: true })} dimension='Marché' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'targetMarket')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.export')} options={t('export', { returnObjects: true })} dimension='Export' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'export')} />
                </li>
                <li>
                  <ResultFilter mode="sidebar" name={t('result.filter.finality')} options={t('finality', { returnObjects: true })} dimension='Economie' onOptionChange={(dimension, category)=> changeReference(dimension, category, 'finality')} />
                </li> {filtered && (
                <li className="filters__clear">
                  <button
                    type="button"
                    className="rf-sidebar-row rf-clear-button"
                    onClick={removeFilter}
                    style={{ textTransform: 'none' }}
                  >
                    <span
                      className="rf-sidebar-label"
                      style={{ textTransform: 'none' }}
                    >
                      {sentenceCase(t('result.filter.reset'))}
                    </span>
                    <span className="rf-sidebar-chevron rf-clear-x">X</span>
                  </button>
                </li>
              )}

              </ul>
            </div>
          </aside>

          <main className='result__center'>
            <p className="filterTitle">{filterTitle}</p>
            <p className='info1Tour'>{t('result.info1')}<strong>{references[currentReferenceIndex]?.sample?.Actual} {t('result.bold1')}</strong> {t('result.info2')} <strong>{references[currentReferenceIndex]?.global?.Mean}</strong></p>
            <ScoreComparisonTable
              t={t}
              companyName={enterprise?.name || ''}
              companyDate={survey?.completedAt ? new Date(survey.completedAt).toLocaleDateString('fr-BE') : ''}
              company={{
                infrastructure: survey?.infrastructure,
                organization:   survey?.humanResources,
                processes:      survey?.processFlow,
                strategy:       survey?.salesProducts,
                global:         (survey?.globalScore ?? survey?.scoreGlobal ?? survey?.global ?? survey?.score),
              }}
              average={{
                infrastructure: references[currentReferenceIndex]?.infrastructure?.Mean,
                organization:   references[currentReferenceIndex]?.humanResources?.Mean,
                processes:      references[currentReferenceIndex]?.processFlow?.Mean,
                strategy:       references[currentReferenceIndex]?.salesProducts?.Mean,
              }}
              comparison={{
                name: enterpriseCompare?.name,
                date: surveyCompare?.completedAt,
                infrastructure: surveyCompare?.infrastructure,
                organization:   surveyCompare?.humanResources,
                processes:      surveyCompare?.processFlow,
                strategy:       surveyCompare?.salesProducts,
                global:         (surveyCompare?.globalScore ?? surveyCompare?.scoreGlobal ?? surveyCompare?.global ?? surveyCompare?.score),
              }}
            />
            <SurveyResultComponent
              filterTitle={filterTitle}
              enterprise={enterprise}
              enterpriseCompare={enterpriseCompare}
              reference={references[currentReferenceIndex]}
              survey={survey}
              surveyCompare={surveyCompare}
              histogramData={histogramData}
              histogramLabels={histogramLabels}
              radarData={data}
            />
          </main>

          <YourResultPanel
            key={i18n.language}
            t={t}
            survey={survey}
            pdfUrl={pdfUrl}
            onDownload={downloadReportHandler}
            onCopyLink={copyShareLinkToClipboard}
            infoTipOpen={isInfoTipOpen}
            onOpenHowTo={() => setHowToOpen(true)}
          />

        </div>
      </div>
      <HowToReadModal open={howToOpen} onClose={() => setHowToOpen(false)} />

      <SurveyTable
        namespace={survey?.namespace}
        surveySessionId={survey?._id}
        token={props.token}
      />
    </div>
  );
}

export default SurveyResultPage;